/**
 * Sprint 23.8 — NeedsAttentionCard (hero section 2).
 *
 * The attention-first heroic moment (Q1 D): a soft-amber card surfacing what
 * needs the supervisor. Hides ENTIRELY when nothing is pending (Q10 — calm
 * state is quiet), so the page reads sparser when all is well. Self-contained:
 * polls `list_workspace_needs_attention` every 30s and routes a click to the
 * first (most-urgent) item's agent.
 *
 * Amber ramp hexes (50/600/800/900) are hard-coded per the visual contract,
 * matching the StatusPill amber elsewhere; dark-mode pairing is a Phase 2 gate.
 */

import { type CSSProperties } from "react";
import { useNavigate } from "react-router";
import { useThemedLabels } from "../../hooks/use-themed-labels";
import { useWorkspaceNeedsAttention } from "../../hooks/use-workspace-needs-attention";
import { formatAge } from "./format";

export function NeedsAttentionCard() {
  const navigate = useNavigate();
  const labels = useThemedLabels();
  const query = useWorkspaceNeedsAttention();
  const items = query.data?.items ?? [];
  const count = items.length;

  // Q10 — hide entirely when empty (and fail silent on error).
  if (count === 0) return null;

  const first = items[0]!;
  const age = formatAge(first.age_seconds);
  const title =
    count === 1 ? "1 thing needs your attention" : `${count} things need your attention`;
  const body = age ? `${first.summary} · ${age}` : first.summary;
  const reviewLabel = count > 1 ? `Review ${count} →` : "Review →";

  function openFirst() {
    if (first.agent_id) navigate(`/agents/${first.agent_id}#tasks`);
  }

  return (
    <div style={card} aria-label={labels.workspaceHomeAttentionTitle}>
      <div style={iconCircle}>
        <i className="ti ti-alert-triangle" style={{ fontSize: 14, color: "#854F0B" }} aria-hidden="true" />
      </div>
      <div style={textCol}>
        <div style={titleText}>{title}</div>
        <div style={bodyText}>{body}</div>
      </div>
      <button type="button" style={reviewBtn} onClick={openFirst}>
        {reviewLabel}
      </button>
    </div>
  );
}

const card: CSSProperties = {
  background: "#FAEEDA",
  borderRadius: "var(--border-radius-md)",
  padding: "12px 14px",
  display: "flex",
  alignItems: "center",
  gap: 12,
};

const iconCircle: CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: "50%",
  background: "rgba(186, 117, 23, 0.15)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
};

const textCol: CSSProperties = {
  flex: 1,
  minWidth: 0,
};

const titleText: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: "#412402",
};

const bodyText: CSSProperties = {
  fontSize: 12,
  color: "#633806",
  lineHeight: 1.5,
  marginTop: 1,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const reviewBtn: CSSProperties = {
  fontSize: 11,
  padding: "5px 10px",
  color: "#412402",
  borderWidth: 1,
  borderStyle: "solid",
  borderColor: "rgba(133, 79, 11, 0.3)",
  borderRadius: "var(--border-radius-sm)",
  background: "transparent",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  whiteSpace: "nowrap",
  flexShrink: 0,
};
